import { AnimatePresence, motion } from "framer-motion";
import { FC, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { ChatBubbleLeftIcon, EnvelopeIcon, PhoneIcon, UserIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";

import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import { useAppSelector } from "hooks/hooks";

interface ContactFormProps {
  onSubmit: SubmitHandler<FormData>;
}

const formSchema = z.object({
  nom: z.string().min(1, { message: "Le nom et le prénom sont obligatoires." }),
  email: z.string().email({ message: "L'adresse e-mail est obligatoire." }),
  message: z.string().min(15, { message: "Le message doit contenir au moins 15 caractères." }),
  phone: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const ContactForm: FC<ContactFormProps> = ({ onSubmit }) => {
  const [loading, setIsLoading] = useState<boolean>(false);

  const contactState = useAppSelector((state) => state.featureContact);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { nom: "", email: "", message: "", phone: "" },
    mode: "onSubmit",
  });

  useEffect(() => {
    switch (contactState.loading) {
      case "pending":
        setIsLoading(true);
        break;
      case "succeeded":
        setIsLoading(false);
        reset();
        break;
      case "failed":
        setIsLoading(false);
        break;
    }
  }, [contactState.loading]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className="p-8 bg-white shadow-lg dark:bg-gray-800 rounded-xl"
    >
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            <UserIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Nom et Prénom *
          </label>
          <Input
            type="text"
            placeholder="Nom et Prénom"
            className={`mt-2 w-full rounded-md border ${errors.nom ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
            {...register("nom")}
          />
          <AnimatePresence>
            {errors.nom && (
              <motion.p
                className="mt-1 text-xs text-red-500"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {errors.nom.message}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            <EnvelopeIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Adresse email *
          </label>
          <Input
            type="email"
            placeholder="exemple@exemple.com"
            className={`mt-2 w-full rounded-md border ${errors.email ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
            {...register("email")}
          />
          <AnimatePresence>
            {errors.email && (
              <motion.p
                className="mt-1 text-xs text-red-500"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {errors.email.message}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            <PhoneIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Numéro de téléphone
          </label>
          <Input
            type="tel"
            placeholder="+33"
            className="w-full mt-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            {...register("phone")}
          />
        </div>

        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            <ChatBubbleLeftIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
            Message *
          </label>
          <Textarea
            className={`mt-2 w-full rounded-md border ${errors.message ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
            rows={6}
            {...register("message")}
          />
          <AnimatePresence>
            {errors.message && (
              <motion.p
                className="mt-1 text-xs text-red-500"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {errors.message.message}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <ButtonPrimary type="submit" loading={loading} className="w-full bg-blue-600 hover:bg-blue-700">
          Envoyer le message
        </ButtonPrimary>
      </form>
    </motion.div>
  );
};

export default ContactForm;
